import { PlanogramItem, PlanogramItemNonCircular, UnrangedPlanogramItem } from "./item";
import { PlanogramBay } from "./bay";
import {
  getEffectiveMerchandisableWidth,
  getMergedMerchandisableWidth,
  getShelfItemWidth,
  isFullyMerchandisable,
} from "@CommonUtils/pog/shelfMerchandising";

interface PlanogramShelfCommon {
  uniqueId: string;
  width: number;
  merchandisableWidth?: number | null;
  depth: number;
  thickness: number;
  offset: number;
  grillHeight: number;
  type: "REGULAR" | "RISER" | "HANGCELL";
  variantRestrictions: PlanogramShelfVariantRestriction[];
  align?: "left" | "right" | "between" | "around";
  /**
   * The maximum load in pounds this board may carry, or null when no source
   * has supplied one — which today is ALWAYS. Do not read the PSA's
   * `Fixture.weightCapacity` into this field: measured across real files it
   * is a dead placeholder (constant 1 or 0), not a fixture spec — see
   * owa-frontend docs/shelf-weight/AGENTS.md §5. The field exists so a future
   * real source (fixture master data, merchant spec sheet) has a home.
   * Optional in data contracts (pogs older than 3.7 lack it), normalized to
   * null in the class.
   */
  weightCapacity?: number | null;
  /**
   * The cap this shelf was judged against by the last pogger run, with its
   * provenance — echoed from `shelf_loads[].limit_g` / `limit_source`.
   *
   * THE ONLY SOURCE OF A SHELF LIMIT: null means no cap was supplied, which
   * renders as NO VERDICT, never as compliant or a guessed default — pogger
   * holds the same line (`limit_source: null`, `resolution_status: "no_cap"`).
   *
   * Distinct from `weightCapacity` (a fixture spec, no data source today).
   * `"request_default"` is the run's own `default_shelf_weight_limit`; a real
   * per-shelf rating comes back as `"shelf"`. Pounds; null for pogs before 3.9.
   */
  effectiveWeightLimit?: EffectiveWeightLimit | null;
}

/** A shelf cap the optimizer actually applied, with its provenance. */
export interface EffectiveWeightLimit {
  pounds: number;
  source: "shelf" | "request_default";
}

export interface PlanogramShelfNonCircular extends PlanogramShelfCommon {
  notchNo: number;
  mergedLeft: string | null;
  mergedRight: string | null;
  items: PlanogramItemNonCircular[];
  bay: string;
}

export interface PlanogramShelfVariantRestriction {
  value: string;
  type: "MAY_HAVE" | "MUST_HAVE" | "MUST_NOT_HAVE";
}

export class PlanogramShelf implements PlanogramShelfCommon {
  uniqueId: string;
  width: number;
  merchandisableWidth?: number | null;
  depth: number;
  thickness: number;
  offset: number;
  grillHeight: number;
  type: "REGULAR" | "RISER" | "HANGCELL";
  variantRestrictions: PlanogramShelfVariantRestriction[];
  mergedLeft: PlanogramShelf | null;
  mergedRight: PlanogramShelf | null;
  items: PlanogramItem[];
  bay: PlanogramBay;
  align?: "left" | "right" | "between" | "around";
  /** See PlanogramShelfCommon — always null today; no data source carries a real limit. */
  weightCapacity: number | null;
  /** See PlanogramShelfCommon — the served cap, or null for no verdict. */
  effectiveWeightLimit: EffectiveWeightLimit | null;

  private notchNo: number;

  constructor(shelf: Omit<PlanogramShelfNonCircular, "bay">, bay: PlanogramBay) {
    this.uniqueId = shelf.uniqueId;
    this.width = shelf.width;
    this.merchandisableWidth = shelf.merchandisableWidth;
    this.depth = shelf.depth;
    this.thickness = shelf.thickness;
    this.offset = shelf.offset;
    this.notchNo = shelf.notchNo;
    this.grillHeight = shelf.grillHeight;
    this.type = shelf.type;
    this.variantRestrictions = [...shelf.variantRestrictions];
    this.mergedLeft = null;
    this.mergedRight = null;
    this.items = shelf.items.map((item) => new PlanogramItem(item, this));
    this.bay = bay;
    this.align = shelf.align || "between";
    this.weightCapacity = shelf.weightCapacity ?? null;
    this.effectiveWeightLimit = shelf.effectiveWeightLimit ?? null;
  }

  getNo = () => {
    return this.bay.shelves.findIndex((shelf) => shelf.uniqueId === this.uniqueId) + 1;
  };

  getName = () => {
    return `${this.bay.getNo()}.${this.getNo()}`;
  };

  getNotchNo = () => {
    return this.notchNo;
  };

  getNewNotchNo = (newY: number) => {
    return Math.max(
      1,
      Math.min(
        this.bay.maxNotch,
        Math.round(
          (newY - this.bay.notchOffset - this.offset + this.thickness) / this.bay.notchSpacing + 1,
        ),
      ),
    );
  };

  getYBottom = () => {
    return (
      this.bay.notchOffset +
      (this.notchNo - 1) * this.bay.notchSpacing +
      this.offset -
      this.thickness
    );
  };

  getYTop = () => {
    const index = this.bay.shelves.findIndex((shelf) => shelf.uniqueId === this.uniqueId);
    const topShelf: PlanogramShelf | undefined = this.bay.shelves[index - 1];

    return topShelf ? topShelf.getYBottom() : this.bay.height;
  };

  getXLeft = () => {
    return this.bay.getXLeft();
  };

  getXRight = () => {
    return this.bay.getXLeft() + this.width;
  };

  getCoordinates = () => {
    return {
      xLeft: this.getXLeft(),
      xRight: this.getXRight(),
      yBottom: this.getYBottom(),
      yTop: this.getYTop(),
    };
  };

  getHeight = () => {
    // Hangcell fixture height is from bottom to bottom shelf.
    if (this.type === "HANGCELL") {
      let bottomShelf: PlanogramShelf | undefined = undefined;

      // Find closest bottom shelf.
      for (const shelfToCheck of this.bay.shelves) {
        if (shelfToCheck.notchNo < this.notchNo) {
          if (bottomShelf === undefined || shelfToCheck.notchNo > bottomShelf.notchNo) {
            bottomShelf = shelfToCheck;
          }
        }
      }

      if (bottomShelf) {
        return this.getYBottom() - bottomShelf.getYBottom();
      }

      return this.getYBottom();
    }

    return this.getYTop() - this.getYBottom();
  };

  /**
   * @returns Array of shelves that are merged with this shelf. Shelves in the array are from left to right.
   */
  getMergedShelves = () => {
    const mergedShelves: PlanogramShelf[] = [];
    const mostLeftMergedShelf = this.getFirstShelf();

    // Loop to most right merged shelf.
    let shelfToGet: PlanogramShelf | null = mostLeftMergedShelf;
    while (shelfToGet !== null) {
      mergedShelves.push(shelfToGet);
      shelfToGet = shelfToGet.mergedRight;
    }

    return mergedShelves;
  };

  getFirstShelf() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let firstShelf: PlanogramShelf = this;
    while (firstShelf.mergedLeft) {
      firstShelf = firstShelf.mergedLeft;
    }
    return firstShelf;
  }

  getTopShelf() {
    let topShelf = null;

    for (const shelf of this.bay.shelves) {
      if (shelf.uniqueId === this.uniqueId) {
        break;
      }

      topShelf = shelf;
    }

    return topShelf;
  }

  getBottomShelf() {
    let currentShelf = null;

    for (const shelf of this.bay.shelves) {
      if (currentShelf !== null) {
        return shelf;
      }

      if (shelf.uniqueId === this.uniqueId) {
        currentShelf = this;
      }
    }

    return null;
  }

  canBeMergedLeft = () => {
    if (this.mergedLeft) return null;
    if (!isFullyMerchandisable(this)) return null;

    for (const bay of this.bay.planogram.bays) {
      for (const shelf of bay.shelves) {
        if (shelf.uniqueId === this.uniqueId) continue;

        if (
          shelf.getXRight() === this.getXLeft() &&
          shelf.getYBottom() === this.getYBottom() &&
          shelf.thickness === this.thickness &&
          isFullyMerchandisable(shelf)
        ) {
          return shelf;
        }
      }
    }

    return null;
  };

  mergeLeft = () => {
    const leftShelf = this.canBeMergedLeft();
    if (leftShelf) {
      leftShelf.mergedRight = this;
      this.mergedLeft = leftShelf;
    }
  };

  unmergeLeft = () => {
    if (this.mergedLeft) {
      this.mergedLeft.mergedRight = null;
      this.mergedLeft = null;
    }
  };

  canBeMergedRight = () => {
    if (this.mergedRight) return null;
    if (!isFullyMerchandisable(this)) return null;

    for (const bay of this.bay.planogram.bays) {
      for (const shelf of bay.shelves) {
        if (shelf.uniqueId === this.uniqueId) continue;

        if (
          shelf.getXLeft() === this.getXRight() &&
          shelf.getYBottom() === this.getYBottom() &&
          shelf.thickness === this.thickness &&
          isFullyMerchandisable(shelf)
        ) {
          return shelf;
        }
      }
    }

    return null;
  };

  mergeRight = () => {
    const rightShelf = this.canBeMergedRight();
    if (rightShelf) {
      rightShelf.mergedLeft = this;
      this.mergedRight = rightShelf;
    }
  };

  unmergeRight = () => {
    if (this.mergedRight) {
      this.mergedRight.mergedLeft = null;
      this.mergedRight = null;
    }
  };

  getMergedWidth = () => {
    const mergedShelves = this.getMergedShelves();
    return mergedShelves.reduce((totalWidth, shelf) => totalWidth + shelf.width, 0);
  };

  /**
   * The items physically standing above THIS board, judged by rendered
   * geometry — the single canonical board assignment for any per-board
   * aggregate (load, counts). Do NOT use `this.items` for that: on a merged
   * run, getCoordinates() lays items out across the whole run and
   * redistributes slack as even gaps, so an item held in one shelf's `items`
   * array can render above a neighbouring board.
   *
   * Every item in the run is assigned to exactly one board: boards own the
   * half-open x-range [boardLeft, boardRight) of their width within the run,
   * and an item belongs to the board its centre x falls in. Items whose
   * centre strays outside the run entirely — an overfilled run pushes items
   * past its end, and nothing today clamps facings — are counted against the
   * first or last board rather than silently dropped.
   *
   * Pegboard placements have no shelf and are invisible to this method by
   * construction; a caller aggregating per-board figures must decide about
   * them explicitly, not assume they are included.
   */
  getItemsPhysicallyOnBoard = (): PlanogramItem[] => {
    const mergedShelves = this.getMergedShelves();

    // This board's x-range within the run.
    let boardLeft = mergedShelves[0].getXLeft();
    for (const shelf of mergedShelves) {
      if (shelf === this) break;
      boardLeft += shelf.width;
    }
    const boardRight = boardLeft + this.width;

    const isFirstBoard = this === mergedShelves[0];
    const isLastBoard = this === mergedShelves[mergedShelves.length - 1];

    const itemsOnBoard: PlanogramItem[] = [];
    for (const shelf of mergedShelves) {
      for (const item of shelf.items) {
        const { xLeft, xRight } = item.getCoordinates();
        const centreX = (xLeft + xRight) / 2;

        const inRange = centreX >= boardLeft && centreX < boardRight;
        const strayedLeft = isFirstBoard && centreX < boardLeft;
        const strayedRight = isLastBoard && centreX >= boardRight;

        if (inRange || strayedLeft || strayedRight) {
          itemsOnBoard.push(item);
        }
      }
    }

    return itemsOnBoard;
  };

  getMerchandisableWidth = () => getEffectiveMerchandisableWidth(this);

  getMergedMerchandisableWidth = () => getMergedMerchandisableWidth(this.getMergedShelves());

  getItemsWidth = () =>
    this.getMergedShelves().reduce(
      (totalWidth, shelf) =>
        totalWidth +
        shelf.items.reduce((shelfWidth, item) => shelfWidth + getShelfItemWidth(item), 0),
      0,
    );

  getOverflowWidth = () => Math.max(0, this.getItemsWidth() - this.getMergedMerchandisableWidth());

  getUnusedWidth = () => {
    return this.getMergedMerchandisableWidth() - this.getItemsWidth();
  };

  /**
   * To get shelf utilization in percentage. Merged shelves are considered as one.
   * @returns Shelf utilization in percentage.
   */
  getUtilizationPercentage = () => {
    const merchandisableWidth = this.getMergedMerchandisableWidth();
    const itemsWidth = this.getItemsWidth();

    if (merchandisableWidth === 0) {
      return itemsWidth === 0 ? 0 : Number.POSITIVE_INFINITY;
    }

    return (itemsWidth * 100) / merchandisableWidth;
  };

  /**
   * Helper function to calculate wether shelf is overflowing.
   * @returns true if shelf utilization is more than 100%.
   */
  isOverflowing = () => {
    return this.getUtilizationPercentage() > 100;
  };

  unrangeItems = () => {
    for (const item of this.items) {
      item.unrange();
    }
  };

  moveShelfToBayNo = (bayNo: number) => {
    if (bayNo > 0 && bayNo <= this.bay.planogram.bays.length) {
      // Remove shelf from current bay.
      const index = this.bay.shelves.findIndex((shelf) => shelf.uniqueId === this.uniqueId);
      this.bay.shelves.splice(index, 1);

      // Add shelf to new bay.
      const newBay = this.bay.planogram.bays[bayNo - 1];
      newBay.shelves.push(this);
      this.bay = newBay;

      newBay.fixShelvesOrder();
    }
  };

  setNotchNo = (notchNo: number) => {
    for (const shelf of this.getMergedShelves()) {
      shelf.notchNo = Math.max(1, Math.min(this.bay.maxNotch, notchNo));
      shelf.bay.fixShelvesOrder();
    }
  };

  delete = () => {
    this.unrangeItems();
    this.bay.shelves = this.bay.shelves.filter((shelf) => shelf.uniqueId !== this.uniqueId);

    if (this.mergedLeft) {
      this.unmergeLeft();
    }

    if (this.mergedRight) {
      this.unmergeRight();
    }
  };

  addItem = (item: PlanogramItem | UnrangedPlanogramItem) => {
    this.addItems([item]);
  };

  addItems = (items: (PlanogramItem | UnrangedPlanogramItem)[]) => {
    for (const item of items) {
      let finalItem: PlanogramItem;

      // Planogram item
      if ("shelf" in item) {
        item.shelf.items = item.shelf.items.filter(
          (shelfItem) => shelfItem.uniqueId !== item.uniqueId,
        );
        finalItem = item;
        item.shelf = this;

        // Unranged item
      } else {
        this.bay.planogram.pog.unrangedItems = this.bay.planogram.pog.unrangedItems.filter(
          (unrangedItem) => unrangedItem.uniqueId !== item.uniqueId,
        );
        finalItem = new PlanogramItem(item, this);
      }

      this.items.push(finalItem);
    }
  };

  sortItemsBy = (sortBy: keyof PlanogramItem) => {
    this.items = this.items.sort((item1, item2) => {
      if (item1[sortBy] === null || item1[sortBy] === undefined) return 0;
      if (item2[sortBy] === null || item2[sortBy] === undefined) return 0;
      if (item1[sortBy]! < item2[sortBy]!) return -1;
      if (item1[sortBy]! > item2[sortBy]!) return 1;
      return 0;
    });
  };
}
